<template>
  <b-card title="File Upload and Processing" class="p-4 mx-auto mt-4" style="width: 50%;">
    <div>
      <label>Main File (MSC/Cunard)</label>
      <b-form-file @change="handleMainFileUpload" accept=".xlsx, .xls, .xlsm" class="mb-3">
        <template #file-name>
          <div class="d-flex align-items-center">
            <b-icon-file-earmark-spreadsheet-fill class="mr-2"></b-icon-file-earmark-spreadsheet-fill>
            <span v-if="mainFileName">{{ mainFileName }}</span>
            <span v-else>Choose a file...</span>
          </div>
        </template>
      </b-form-file>
    </div>
    <div>
      <label>Feeder File (MSC Only)</label>
      <b-form-file @change="handleFeederFileUpload" accept=".xlsx, .xls, .xlsm">
        <template #file-name>
          <div class="d-flex align-items-center">
            <b-icon-file-earmark-spreadsheet-fill class="mr-2"></b-icon-file-earmark-spreadsheet-fill>
            <span v-if="feederFileName">{{ feederFileName }}</span>
            <span v-else>Choose a file...</span>
          </div>
        </template>
      </b-form-file>
    </div>

    <div v-if="processedData.length > 0" class="mt-3">
      <b-button @click="exportToExcel" variant="primary">MSC Excel</b-button>
    </div>
    <div v-if="processedDataCunard.length > 0" class="mt-3">
      <b-button @click="exportToExcelCunard" variant="success">Cunard Excel</b-button>
    </div>
  </b-card>
</template>

<script>
import { defineComponent } from 'vue';
import * as XLSX from 'xlsx';

export default defineComponent({
  name: 'FileUpload',
  data() {
    return {
      mainFileData: [],
      feederFileData: [],
      processedData: [],
      processedDataCunard: [],
      mainFileName: '',
      feederFileName: '',
    };
  },
  methods: {
    async handleMainFileUpload(event) {
      const file = event.target.files[0];
      if (file) {
        try {
          const data = await this.readFile(file);
          this.mainFileData = data;
          this.mainFileName = file.name;
          this.processFiles();
          this.$bvToast.toast('Main file processed successfully', {
            title: 'Success',
            variant: 'success',
            solid: true,
          });
        } catch (error) {
          console.error('Error processing main file:', error);
          this.$bvToast.toast('Failed to process main file. Please ensure it’s a valid Excel file.', {
            title: 'Error',
            variant: 'danger',
            solid: true,
          });
        }
      }
    },
    async handleFeederFileUpload(event) {
      const file = event.target.files[0];
      if (file) {
        try {
          const data = await this.readFile(file);
          this.feederFileData = data;
          this.feederFileName = file.name;
          this.processFiles();
          this.$bvToast.toast('Feeder file processed successfully', {
            title: 'Success',
            variant: 'success',
            solid: true,
          });
        } catch (error) {
          console.error('Error processing feeder file:', error);
          this.$bvToast.toast('Failed to process feeder file. Please ensure it’s a valid Excel file.', {
            title: 'Error',
            variant: 'danger',
            solid: true,
          });
        }
      }
    },
    readFile(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { header: 1 });
          resolve(worksheet);
        };
        reader.onerror = (error) => reject(error);
        reader.readAsArrayBuffer(file);
      });
    },
    processFiles() {
      if (this.mainFileData.length === 0) return;

      // Process main file data for MSC and Cunard
      const mscData = this.processData(this.mainFileData);
      const cunardData = this.processDataCunard(this.mainFileData);

      // Process feeder file data
      const feederMap = this.processFeederData(this.feederFileData);

      // Merge feeder data with MSC data only
      this.processedData = this.mergeData(mscData, feederMap);
      this.processedDataCunard = cunardData; // No feeder merge for Cunard
    },
processData(data) {
  const seen = new Set();
  return data
    .slice(1)
    .map((row) => ({
      bookingNumber: row[0] ? row[0].toString().trim() : '',
      guestsLastName: row[3] ? row[3].toString().trim() : '',
      firstName: row[4] ? row[4].toString().trim() : '',
      flightNumber: `${row[8] ? row[8].toString().trim() : ''}${row[9] ? row[9].toString().trim() : ''}`,
      language: row[6] ? row[6].toString().trim() : '',
      departureAirport: row[10] ? row[10].toString().trim() : '',
      arrivalAirport: row[13] ? row[13].toString().trim() : '',
      cabinNumber: row[17] ? row[17].toString().trim() : '',
      note: row[20] ? row[20].toString().trim() : '',
      remarks: row[21] ? row[21].toString().trim() : '',
      departureDate: this.convertDateFormat(row[11]),
      arrivalDate: this.convertDateFormat(row[14]),
      departureTime: this.formatExcelTime(row[12]),
      arrivalTime: this.formatExcelTime(row[15]),
      cruiseLine: 'MSC',
      feeder: '', // Initialize feeder field for MSC
    }))
    .filter((row) => {
      // Check existing filter condition
      if (!(row.bookingNumber || row.flightNumber) || row.cabinNumber === '') {
        return false;
      }
      // Deduplicate based on all field values
      const rowString = JSON.stringify(row);
      if (seen.has(rowString)) {
        return false;
      }
      seen.add(rowString);
      return true;
    });
},
    processDataCunard(data) {
      return data.slice(1).map((row) => ({
        bookingNumber: row[8] ? row[8].toString().trim() : '',
        guestsLastName: row[13] ? row[13].toString().trim() : '',
        firstName: row[12] ? row[12].toString().trim() : '',
        flightNumber: `${row[26] ? row[26].toString().trim() : ''}${row[27] ? row[27].toString().trim() : ''}`,
        language: '',
        departureAirport: row[28] ? row[28].toString().trim() : '',
        arrivalAirport: row[31] ? row[31].toString().trim() : '',
        cabinNumber: row[9] ? row[9].toString().trim() : '',
        note: row[35] ? row[35].toString().trim() : '',
        remarks: `${row[36] ? row[36].toString().trim() : ''}${row[37] ? row[37].toString().trim() : ''}`,
        departureDate: this.convertDateFormatCunard(row[29] ? row[29].toString().trim() : ''),
        arrivalDate: this.convertDateFormatCunard(row[32] ? row[32].toString().trim() : ''),
        departureTime: this.convertTimeFormatCunard(row[30] ? row[30].toString().trim() : ''),
        arrivalTime: this.convertTimeFormatCunard(row[33] ? row[33].toString().trim() : ''),
        cruiseLine: 'Cunard',
      })).filter(row => (row.bookingNumber || row.flightNumber) && row.cabinNumber !== 'CABIN' && row.cabinNumber !== '');
    },
    processFeederData(data) {
      const feederMap = new Map();
      data.slice(1).forEach((row) => {
        const bookingNumber = row[14] ? row[14].toString().trim() : ''; // BKGNO
        const cabinNumber = row[1] ? row[1].toString().trim() : ''; // CABIN
        const feeder = row[9] ? row[9].toString().trim() : ''; // FEEDER
        if (bookingNumber && cabinNumber) {
          const key = `${bookingNumber}|${cabinNumber}`;
          feederMap.set(key, feeder);
        }
      });
      return feederMap;
    },
    mergeData(data, feederMap) {
      return data.map(row => {
        if (row.cruiseLine === 'MSC') {
          return {
            ...row,
            feeder: feederMap.get(`${row.bookingNumber}|${row.cabinNumber}`) || '',
          };
        }
        return row;
      });
    },
    convertDateFormat(dateString) {
      if (!dateString) return '';

      const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (isoDateRegex.test(dateString)) {
        return dateString;
      }

      const usDateRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
      const match = dateString.match(usDateRegex);
      if (match) {
        const month = match[1].padStart(2, '0');
        const day = match[2].padStart(2, '0');
        const year = match[3];
        return `${year}-${month}-${day}`;
      }

      return '';
    },
    convertDateFormatCunard(dateString) {
      const dateParts = dateString.split('-');
      if (dateParts.length === 3) {
        const day = dateParts[0].padStart(2, '0');
        const month = this.getMonthNumber(dateParts[1].toUpperCase()).padStart(2, '0');
        const year = '20' + dateParts[2];
        return `${year}-${month}-${day}`;
      }
      return '';
    },
    convertTimeFormatCunard(timeString) {
      const timeParts = timeString.split(':');
      if (timeParts.length === 2) {
        const hours = timeParts[0].padStart(2, '0');
        const minutes = timeParts[1].padStart(2, '0');
        return `${hours}:${minutes}:00`;
      }
      return '';
    },
    getMonthNumber(month) {
      const months = {
        JAN: '01', FEB: '02', MAR: '03', APR: '04', MAY: '05', JUN: '06',
        JUL: '07', AUG: '08', SEP: '09', OCT: '10', NOV: '11', DEC: '12'
      };
      return months[month] || '';
    },
    formatExcelTime(timeValue) {
      if (timeValue == null || timeValue === '') return '';

      if (typeof timeValue === 'number' && !isNaN(timeValue)) {
        const totalMinutes = Math.round(timeValue * 24 * 60);
        const hours = String(Math.floor(totalMinutes / 60)).padStart(2, '0');
        const minutes = String(totalMinutes % 60).padStart(2, '0');
        return `${hours}:${minutes}:00`;
      }

      if (Object.prototype.toString.call(timeValue) === '[object Date]') {
        const hours = String(timeValue.getHours()).padStart(2, '0');
        const minutes = String(timeValue.getMinutes()).padStart(2, '0');
        return `${hours}:${minutes}:00`;
      }

      const timeString = timeValue.toString().trim();
      const hhmmssRegex = /^(\d{1,2}):(\d{2}):(\d{2})$/;
      if (hhmmssRegex.test(timeString)) {
        return timeString;
      }

      const timeRegex = /^(\d{1,2}):(\d{2})\s?(AM|PM)?$/i;
      const match = timeString.match(timeRegex);
      if (match) {
        let hours = parseInt(match[1], 10);
        const minutes = match[2];
        const period = match[3] ? match[3].toUpperCase() : null;
        if (period === 'PM' && hours !== 12) hours += 12;
        else if (period === 'AM' && hours === 12) hours = 0;
        return `${String(hours).padStart(2, '0')}:${minutes}:00`;
      }

      return '';
    },
    exportToExcel() {
      if (this.processedData.length === 0) {
        this.$bvToast.toast('No MSC data to export', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        });
        return;
      }

      const header = [
        'bookingNumber', 'guestsLastName', 'firstName', 'flightNumber', 'language',
        'departureAirport', 'arrivalAirport', 'cabinNumber', 'note', 'remarks',
        'departureDate', 'arrivalDate', 'departureTime', 'arrivalTime', 'cruiseLine', 'feeder'
      ];

      const wsData = this.processedData.map(entry => {
        const row = [];
        header.forEach(key => row.push(entry[key] || ''));
        return row;
      });

      const ws = XLSX.utils.aoa_to_sheet([header, ...wsData]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

     let outputFileName = 'Processed_MSC.xlsx'; // Default fallback
      if (this.mainFileName) {
        const fileNameLower = this.mainFileName.toLowerCase();
        if (fileNameLower.includes('disembarking') || fileNameLower.includes('debark')) {
          outputFileName = 'Processed_MSC_Debark.xlsx';
        } else if (fileNameLower.includes('embark')) {
          outputFileName = 'Processed_MSC_Embark.xlsx';
        } else {
          outputFileName = `Processed_MSC_${this.mainFileName}`;
        }
      }

      XLSX.writeFile(wb, outputFileName);
      this.$bvToast.toast('MSC data exported successfully', {
        title: 'Success',
        variant: 'success',
        solid: true,
      });
    },
    exportToExcelCunard() {
      if (this.processedDataCunard.length === 0) {
        this.$bvToast.toast('No Cunard data to export', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        });
        return;
      }

      const header = [
        'bookingNumber', 'guestsLastName', 'firstName', 'flightNumber', 'language',
        'departureAirport', 'arrivalAirport', 'cabinNumber', 'note', 'remarks',
        'departureDate', 'arrivalDate', 'departureTime', 'arrivalTime', 'cruiseLine'
      ];

      const wsData = this.processedDataCunard.map(entry => {
        const row = [];
        header.forEach(key => row.push(entry[key] || ''));
        return row;
      });

      const ws = XLSX.utils.aoa_to_sheet([header, ...wsData]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

      let outputFileName = `Processed_Cunard_${this.mainFileName}`;
      if (this.mainFileName) {
        const fileNameLower = this.mainFileName.toLowerCase();
        if (fileNameLower.includes('embark')) {
          outputFileName = `Processed_Cunard_Embark.xlsx`;
        } else if (fileNameLower.includes('disembarking') || fileNameLower.includes('debark')) {
          outputFileName = `Processed_Cunard_Debark.xlsx`;
        }
      }

      XLSX.writeFile(wb, outputFileName);
      this.$bvToast.toast('Cunard data exported successfully', {
        title: 'Success',
        variant: 'success',
        solid: true,
      });
    },
  },
});
</script>

<style scoped>
.mx-auto {
  margin-left: auto;
  margin-right: auto;
}
</style>
